
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {LegalDocumentGroup} from './LegalDocumentGroup';
import {FieldModelLegalDocumentGroup} from './LegalDocumentGroup';
import {LegalDocumentDescription} from './LegalDocumentDescription';
import {FieldModelLegalDocumentDescription} from './LegalDocumentDescription';

/**
 * LegalDocumentGroupWithDocumentsDescriptions struct
 */
class LegalDocumentGroupWithDocumentsDescriptions {
  /**
   * Initialize struct
   * @param {!LegalDocumentGroup=} group
   * @param {!Array=} documents
   * @constructor
   */
  constructor (group = new LegalDocumentGroup(), documents = []) {
    this.Group = group
    this.Documents = documents
  }

  /**
   * Copy struct (shallow copy)
   * @this {!LegalDocumentGroupWithDocumentsDescriptions}
   * @param {!LegalDocumentGroupWithDocumentsDescriptions} other Other struct
   * @returns {!LegalDocumentGroupWithDocumentsDescriptions} This struct
   */
  copy (other) {
    if (other.Group != null) {
      this.Group = LegalDocumentGroup.fromObject(other.Group)
    } else {
      this.Group = null
    }
    if (other.Documents != null) {
      this.Documents = []
      for (let item of other.Documents) {
        if (item != null) {
          let tempItem
          tempItem = LegalDocumentDescription.fromObject(item)
          this.Documents.push(tempItem)
        } else {
          this.Documents.push(null)
        }
      }
    } else {
      this.Documents = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!LegalDocumentGroupWithDocumentsDescriptions}
   * @returns {!LegalDocumentGroupWithDocumentsDescriptions} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new LegalDocumentGroupWithDocumentsDescriptionsModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new LegalDocumentGroupWithDocumentsDescriptionsModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!LegalDocumentGroupWithDocumentsDescriptions}
   * @param {!LegalDocumentGroupWithDocumentsDescriptions} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof LegalDocumentGroupWithDocumentsDescriptions)) {
      throw new TypeError('Instance of LegalDocumentGroupWithDocumentsDescriptions is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!LegalDocumentGroupWithDocumentsDescriptions}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Group: ((this.Group != null) ? this.Group : null),
      Documents: ((this.Documents != null) ? Array.from(this.Documents, item => ((item != null) ? item : null)) : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return LegalDocumentGroupWithDocumentsDescriptions.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!LegalDocumentGroupWithDocumentsDescriptions} other Object value
   * @returns {!LegalDocumentGroupWithDocumentsDescriptions} Created struct
   */
  static fromObject (other) {
    return new LegalDocumentGroupWithDocumentsDescriptions().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!LegalDocumentGroupWithDocumentsDescriptions}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return LegalDocumentGroupWithDocumentsDescriptions.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!LegalDocumentGroupWithDocumentsDescriptions}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6596
  }
}

export { LegalDocumentGroupWithDocumentsDescriptions };

/**
 * Fast Binary Encoding LegalDocumentGroupWithDocumentsDescriptions field model
 */
class FieldModelLegalDocumentGroupWithDocumentsDescriptions extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Group = new FieldModelLegalDocumentGroup(buffer, 4 + 4)
    this.Documents = new fbe.FieldModelVector(new FieldModelLegalDocumentDescription(buffer, this.Group.fbeOffset + this.Group.fbeSize), buffer, this.Group.fbeOffset + this.Group.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelLegalDocumentGroupWithDocumentsDescriptions}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelLegalDocumentGroupWithDocumentsDescriptions}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Group.fbeSize + this.Documents.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelLegalDocumentGroupWithDocumentsDescriptions}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Group.fbeExtra + this.Documents.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelLegalDocumentGroupWithDocumentsDescriptions}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelLegalDocumentGroupWithDocumentsDescriptions.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelLegalDocumentGroupWithDocumentsDescriptions}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6596
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelLegalDocumentGroupWithDocumentsDescriptions}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelLegalDocumentGroupWithDocumentsDescriptions}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Group.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Group.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Group.fbeSize

    if ((fbeCurrentSize + this.Documents.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Documents.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Documents.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelLegalDocumentGroupWithDocumentsDescriptions}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelLegalDocumentGroupWithDocumentsDescriptions}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelLegalDocumentGroupWithDocumentsDescriptions}
   * @param {!LegalDocumentGroupWithDocumentsDescriptions} fbeValue Default value, defaults is new LegalDocumentGroupWithDocumentsDescriptions()
   * @returns {!LegalDocumentGroupWithDocumentsDescriptions} LegalDocumentGroupWithDocumentsDescriptions value
   */
  get (fbeValue = new LegalDocumentGroupWithDocumentsDescriptions()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelLegalDocumentGroupWithDocumentsDescriptions}
   * @param {!LegalDocumentGroupWithDocumentsDescriptions} fbeValue LegalDocumentGroupWithDocumentsDescriptions value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Group.fbeSize) <= fbeStructSize) {
      fbeValue.Group = this.Group.get()
    } else {
      fbeValue.Group = new LegalDocumentGroup()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Group.fbeSize

    if ((fbeCurrentSize + this.Documents.fbeSize) <= fbeStructSize) {
      this.Documents.get(fbeValue.Documents)
    } else {
      fbeValue.Documents.length = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Documents.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelLegalDocumentGroupWithDocumentsDescriptions}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelLegalDocumentGroupWithDocumentsDescriptions}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelLegalDocumentGroupWithDocumentsDescriptions}
   * @param {!LegalDocumentGroupWithDocumentsDescriptions} fbeValue LegalDocumentGroupWithDocumentsDescriptions value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelLegalDocumentGroupWithDocumentsDescriptions}
   * @param {LegalDocumentGroupWithDocumentsDescriptions} fbeValue LegalDocumentGroupWithDocumentsDescriptions value
   */
  setFields (fbeValue) {
    this.Group.set(fbeValue.Group)
    this.Documents.set(fbeValue.Documents)
  }
}

export { FieldModelLegalDocumentGroupWithDocumentsDescriptions };

/**
 * Fast Binary Encoding LegalDocumentGroupWithDocumentsDescriptions model
 */
class LegalDocumentGroupWithDocumentsDescriptionsModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelLegalDocumentGroupWithDocumentsDescriptions(this.buffer, 4)
  }

  /**
   * Get the LegalDocumentGroupWithDocumentsDescriptions model
   * @this {!LegalDocumentGroupWithDocumentsDescriptionsModel}
   * @returns {!FieldModelLegalDocumentGroupWithDocumentsDescriptions} model LegalDocumentGroupWithDocumentsDescriptions model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!LegalDocumentGroupWithDocumentsDescriptionsModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!LegalDocumentGroupWithDocumentsDescriptionsModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return LegalDocumentGroupWithDocumentsDescriptionsModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!LegalDocumentGroupWithDocumentsDescriptionsModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelLegalDocumentGroupWithDocumentsDescriptions.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!LegalDocumentGroupWithDocumentsDescriptionsModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!LegalDocumentGroupWithDocumentsDescriptionsModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!LegalDocumentGroupWithDocumentsDescriptionsModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!LegalDocumentGroupWithDocumentsDescriptionsModel}
   * @param {!LegalDocumentGroupWithDocumentsDescriptions} value LegalDocumentGroupWithDocumentsDescriptions value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!LegalDocumentGroupWithDocumentsDescriptionsModel}
   * @param {!LegalDocumentGroupWithDocumentsDescriptions} value LegalDocumentGroupWithDocumentsDescriptions value, defaults is new LegalDocumentGroupWithDocumentsDescriptions()
   * @return {!object} Deserialized LegalDocumentGroupWithDocumentsDescriptions value and its size
   */
  deserialize (value = new LegalDocumentGroupWithDocumentsDescriptions()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new LegalDocumentGroupWithDocumentsDescriptions(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new LegalDocumentGroupWithDocumentsDescriptions(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!LegalDocumentGroupWithDocumentsDescriptionsModel}
   * @param {!number} prev Previous LegalDocumentGroupWithDocumentsDescriptions model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { LegalDocumentGroupWithDocumentsDescriptionsModel };
